import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminWebshopsTable } from '../-components/AdminWebshopsTable';
import { CreateWebshopDialog } from '../-components/CreateWebshopDialog';
import { WebshopDetailSheet } from '../-components/WebshopDetailSheet';
import { WebshopsHelpSheet } from '../-components/WebshopsHelpSheet';
import { adminWebshopsQueryOptions } from '../-hooks/useAdminWebshops';
import { useAdminWebshopsPage } from '../-hooks/useAdminWebshopsPage';

const webshopsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(200), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/webshops/(pages)/')({
  validateSearch: webshopsSearch,
  // Read off the URL rather than fixed to the first page, so a deep link with a
  // filter prefetches the rows it will actually show.
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    filters: search.filters ?? {},
  }),
  // Swallowed: a rejected loader hands the route to the router's error
  // boundary, and the table already reports a failed load as a banner.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminWebshopsQueryOptions(deps))
      .catch(() => undefined),
  component: WebshopsPage,
});

function WebshopsPage() {
  const page = useAdminWebshopsPage();

  return (
    <PageContainer>
      <AdminWebshopsTable page={page} />
      <CreateWebshopDialog page={page} />
      <WebshopDetailSheet page={page} />
      <WebshopsHelpSheet
        open={page.isHelpOpen}
        onOpenChange={page.setHelpOpen}
      />
    </PageContainer>
  );
}
